import type { UserProfile, UserSettings } from '@/types/auth';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Token response type
interface AuthResponse {
  access_token: string;
  refresh_token: string;
}

interface AuthState {
  accessToken: string;
  refreshToken: string;
  user: UserProfile | null;
  isInitializing: boolean;
  hasHydrated: boolean;
  setTokenAuth: (tokens: AuthResponse) => void;
  setUser: (user: UserProfile) => void;
  clearAuth: () => void;
  setInitializing: (isInitializing: boolean) => void;
  patchSettings: (patch: Partial<UserSettings>) => void;
  setHasHydrated: (state: boolean) => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: '',
      refreshToken: '',
      isInitializing: true,
      hasHydrated: false,
      user: {
        userId: '',
        userName: '',
        email: '',
        role: 'user',
      },
      setTokenAuth: (tokens: AuthResponse) => {
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
        });
      },

      setUser: (user: UserProfile) => {
        set({ user });
      },

      setInitializing: (isInitializing: boolean) => {
        set({ isInitializing });
      },

      patchSettings: (patch: Partial<UserSettings>) => {
        const { user } = get();
        if (!user) return;
        set({
          user: {
            ...user,
            settings: {
              lang: patch.lang !== undefined ? patch.lang : (user.settings?.lang ?? null),
              publish:
                patch.publish !== undefined ? patch.publish : (user.settings?.publish ?? null),
            },
          },
        });
      },

      setHasHydrated: (state: boolean) => {
        set({ hasHydrated: state });
      },

      clearAuth: () => {
        set({
          accessToken: '',
          refreshToken: '',
          user: null,
        });
      },

      isAuthenticated: () => {
        const state = get();
        return !!state.accessToken;
      },
    }),
    {
      name: 'auth-storage',
      version: 4,
      partialize: (state) => ({
        accessToken: state.accessToken || undefined,
        refreshToken: state.refreshToken || undefined,
        user: state.user || undefined,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
